import { callGeotabAPI } from '../api/geotab-client';
import { fetchRangeChunked } from '../api/fetch-range';
import type { AuditRecord, GeotabUser, LoginAuditRow } from './login-audit-types';
import {
  auditSortCol,
  auditSortAsc,
  setAuditRawRows,
} from './login-audit-page';
import { renderLoginAuditResults, showAuditState } from './login-audit-results';
import { isDomainExcluded } from './login-audit-exclusions';

export async function runLoginAuditAnalysis(): Promise<void> {
  const fromVal = (document.getElementById('laFrom') as HTMLInputElement).value;
  const toVal   = (document.getElementById('laTo')   as HTMLInputElement).value;
  if (!fromVal || !toVal) { alert('Please select a date range.'); return; }

  const fromDate = new Date(fromVal + 'T00:00:00');
  const toDate   = new Date(toVal   + 'T23:59:59');
  if (fromDate >= toDate) { alert('Start date must be before end date.'); return; }

  const today   = new Date();
  const todayMs = today.getTime();

  const runBtn = document.getElementById('laRunBtn') as HTMLButtonElement;
  runBtn.disabled  = true;
  runBtn.innerHTML = '<span style="opacity:0.6">Loading…</span>';
  showAuditState('loading', 'Fetching users and login events…');

  try {
    // Fetch users and ALL audit records for the selected range in parallel.
    // Use fetchRangeChunked with monthly chunks so each API call covers a small enough
    // window that it never hits Geotab's 50 000-record cap — a single call for a 6-month
    // range returns ALL audit types (logins, device changes, etc.) and easily exceeds 50k,
    // silently truncating the data and producing wrong login counts.
    const [users, allAuditRecords] = await Promise.all([
      callGeotabAPI<GeotabUser[]>('Get', {
        typeName: 'User',
        resultsLimit: 50000,
      }),
      fetchRangeChunked<AuditRecord>({
        fromISO:      fromDate.toISOString(),
        toISO:        toDate.toISOString(),
        perCallLimit: 50000,
        // No server-side name filter — confirmed working without it; adds risk of 0 results.
        fetch: (f, t) =>
          callGeotabAPI<AuditRecord[]>('Get', {
            typeName: 'Audit',
            search: { fromDate: f, toDate: t },
            resultsLimit: 50000,
          }),
        initialChunkDays: 30,   // monthly chunks → ~6 calls for a 6-month range
        concurrency: 4,          // gentle on Geotab's rate limiter
      }),
    ]);

    // Filter to successful logins. Per Geotab's official audit log reference:
    //   name === "User login"  → successful login
    //   name === "User failed login attempt" → failed (excluded)
    //   name === "User logout" → logout (excluded)
    // We check includes('login') but NOT includes('logout') or includes('failed')
    // as a safety net in case the exact name differs in this Geotab version.
    const loginRecords = allAuditRecords.filter((r) => {
      const n = r.name?.toLowerCase() ?? '';
      return n.includes('login') && !n.includes('logout') && !n.includes('failed') && !n.includes('locked');
    });

    const rangeFromMs = fromDate.getTime();
    const rangeToMs   = toDate.getTime();

    // Geotab Audit.userName = the login email of the actor (matches User.name).
    const lastLoginByUserName  = new Map<string, string>();
    const rangeCountByUserName = new Map<string, number>();

    for (const record of loginRecords) {
      const uName = record.userName;
      if (!uName) continue;

      const prev = lastLoginByUserName.get(uName);
      if (!prev || record.dateTime > prev) lastLoginByUserName.set(uName, record.dateTime);

      const ts = new Date(record.dateTime).getTime();
      if (ts >= rangeFromMs && ts <= rangeToMs) {
        rangeCountByUserName.set(uName, (rangeCountByUserName.get(uName) ?? 0) + 1);
      }
    }

    // One row per currently-active user. Archived users have activeTo in the past.
    // Internal/vendor accounts (@et-envue.com, @geotab.com) are excluded — they're never
    // "fleet users" and would otherwise pollute the KPIs, chart, and table.
    const rows: LoginAuditRow[] = [];
    for (const user of users) {
      if (user.activeTo && new Date(user.activeTo).getTime() < todayMs) continue;
      if (isDomainExcluded(user.name)) continue;

      // User.name is the login email — same value as Audit.userName.
      const lastLoginISO  = lastLoginByUserName.get(user.name)  ?? null;
      const loginsInRange = rangeCountByUserName.get(user.name) ?? 0;

      const displayName =
        [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name;

      rows.push({ userName: user.name, userId: user.id, displayName, isDriver: user.isDriver, loginsInRange, lastLoginISO });
    }

    setAuditRawRows(rows);

    const metaEl = document.getElementById('laMeta');
    if (metaEl) {
      const activeInRange = rows.filter((r) => r.loginsInRange > 0).length;
      metaEl.textContent =
        `${fromDate.toLocaleDateString()} – ${toDate.toLocaleDateString()}` +
        ` · ${loginRecords.length.toLocaleString()} login events` +
        ` · ${activeInRange} users active in range`;
    }

    renderLoginAuditResults(rows, auditSortCol, auditSortAsc);
    showAuditState('results');

    const exportBtn = document.getElementById('laExportBtn') as HTMLElement;
    exportBtn.style.display = '';

  } catch (err) {
    showAuditState('error', 'Error: ' + (err as Error).message);
  } finally {
    runBtn.disabled  = false;
    runBtn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><polygon points="3,1 13,7 3,13" fill="currentColor"/></svg> Run Audit';
  }
}
